// src/redux/creatorsCrmSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  canonicalCreatorCrmKey,
  createCreatorCrm,
  createCreatorCrmActivity,
  getCreatorCrm,
  listCreatorCrm,
  listCreatorCrmActivities,
  normalizeCreatorCrmRecord,
  updateCreatorCrm,
} from "../services/creatorCrmService";

function norm(value) {
  return String(value ?? "").trim();
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function canonicalKey(valueOrRow) {
  return canonicalCreatorCrmKey(valueOrRow);
}

function normalizeRow(row = {}) {
  const normalized = normalizeCreatorCrmRecord(
    isObject(row) ? row : {}
  );

  return {
    ...normalized,
    creatorKey: canonicalKey(normalized),
    crmStage: norm(normalized.crmStage || normalized.status || "new"),
    crmStatus: norm(normalized.crmStatus || normalized.status || "new"),
    nextStep: norm(
      normalized.nextStep ||
        normalized.nextAction ||
        normalized.metadata?.nextStep
    ),
    nextAction: norm(
      normalized.nextAction ||
        normalized.nextStep ||
        normalized.metadata?.nextStep
    ),
    notes: norm(
      normalized.notes ||
        normalized.latestNote ||
        normalized.notesPreview
    ),
    notesPreview: norm(
      normalized.notesPreview ||
        normalized.latestNote ||
        normalized.notes
    ),
    lastContactedAt: norm(
      normalized.lastContactedAt ||
        normalized.lastTouchedAt
    ),
    lastTouchedAt: norm(
      normalized.lastTouchedAt ||
        normalized.lastContactedAt
    ),
    offerId: norm(
      normalized.offerId ||
        normalized.crmOfferId ||
        normalized.linkedOfferIds?.[0]
    ),
    crmOfferId: norm(
      normalized.crmOfferId ||
        normalized.offerId ||
        normalized.linkedOfferIds?.[0]
    ),
  };
}

function mergeRows(existingRow, incomingRow) {
  const existing = normalizeRow(existingRow || {});
  const incoming = normalizeRow(incomingRow || {});

  return normalizeRow({
    ...existing,
    ...incoming,
    identity: {
      ...(isObject(existing.identity) ? existing.identity : {}),
      ...(isObject(incoming.identity) ? incoming.identity : {}),
    },
    metadata: {
      ...(isObject(existing.metadata) ? existing.metadata : {}),
      ...(isObject(incoming.metadata) ? incoming.metadata : {}),
    },
  });
}

function dedupeRows(rows) {
  const byKey = new Map();
  const withoutKey = [];

  (Array.isArray(rows) ? rows : []).forEach((rawRow) => {
    const row = normalizeRow(rawRow);
    const key = canonicalKey(row);

    if (!key) {
      withoutKey.push(row);
      return;
    }

    const existing = byKey.get(key);
    byKey.set(key, existing ? mergeRows(existing, row) : row);
  });

  return [...byKey.values(), ...withoutKey];
}

function upsertRow(items, incomingRow) {
  const row = normalizeRow(incomingRow || {});
  const key = canonicalKey(row);
  const source = Array.isArray(items) ? items : [];

  if (!key) {
    return dedupeRows([row, ...source]);
  }

  let replaced = false;

  const next = source.map((existingRow) => {
    const existingKey = canonicalKey(existingRow);

    if (existingKey !== key) {
      return normalizeRow(existingRow);
    }

    replaced = true;
    return mergeRows(existingRow, row);
  });

  if (!replaced) {
    next.unshift(row);
  }

  return dedupeRows(next);
}

function normalizeActivity(raw = {}) {
  const activity =
    (isObject(raw?.item) && raw.item) ||
    (isObject(raw?.activity) && raw.activity) ||
    (isObject(raw) ? raw : {});

  const activityType = norm(
    activity.activityType ||
      activity.type ||
      "note"
  );

  const note = norm(
    activity.note ||
      activity.message ||
      activity.text ||
      activity.notes
  );

  const createdAt = norm(
    activity.createdAt ||
      activity.timestamp ||
      activity.occurredAt ||
      activity.happenedAt
  );

  return {
    ...activity,
    activityId: norm(
      activity.activityId ||
        activity.id ||
        activity._id
    ),
    activityType,
    type: activityType,
    note,
    message: note,
    happenedAt: norm(activity.happenedAt || createdAt),
    createdAt,
    actorName: norm(
      activity.actorName ||
        activity.createdBy ||
        activity.userName
    ),
  };
}

function prependActivity(existingItems, incomingActivity) {
  const normalized = normalizeActivity(incomingActivity);
  const activityId = norm(normalized.activityId);
  const items = Array.isArray(existingItems) ? [...existingItems] : [];

  if (!activityId) {
    return [normalized, ...items];
  }

  const index = items.findIndex(
    (item) =>
      norm(item?.activityId || item?.id || item?._id) === activityId
  );

  if (index >= 0) {
    items[index] = {
      ...items[index],
      ...normalized,
    };
    return items;
  }

  return [normalized, ...items];
}

function extractCampaignId(input = {}) {
  return norm(
    input?.campaignId ||
      input?.params?.campaignId ||
      input?.payload?.campaignId
  );
}

function extractCreatorKey(input = {}) {
  return canonicalKey(
    input?.creatorKey ||
      input?.payload?.creatorKey ||
      input?.payload
  );
}

function resolveActionKey(action) {
  return canonicalKey(
    action?.payload?.creatorKey ||
      action?.meta?.arg?.creatorKey ||
      action?.meta?.arg?.payload?.creatorKey ||
      action?.meta?.arg?.payload
  );
}

function errorMessage(action, fallback) {
  if (typeof action?.payload === "string") {
    return action.payload;
  }

  return (
    action?.payload?.message ||
    action?.error?.message ||
    fallback
  );
}

function moveKeyedStateEntry(container, fromKey, toKey) {
  if (!container || !fromKey || !toKey || fromKey === toKey) return;

  if (Object.prototype.hasOwnProperty.call(container, fromKey)) {
    container[toKey] = container[fromKey];
    delete container[fromKey];
  }
}

function syncKeyAliases(state, rawKey, canonical) {
  if (!rawKey || !canonical || rawKey === canonical) return;

  moveKeyedStateEntry(state.savingByKey, rawKey, canonical);
  moveKeyedStateEntry(state.updateErrorByKey, rawKey, canonical);
  moveKeyedStateEntry(state.detailByKey, rawKey, canonical);
  moveKeyedStateEntry(state.detailLoadingByKey, rawKey, canonical);
  moveKeyedStateEntry(state.detailErrorByKey, rawKey, canonical);
  moveKeyedStateEntry(state.activitiesByKey, rawKey, canonical);
  moveKeyedStateEntry(state.activitiesLoadingByKey, rawKey, canonical);
  moveKeyedStateEntry(state.activitiesErrorByKey, rawKey, canonical);
  moveKeyedStateEntry(state.activitiesUnavailableByKey, rawKey, canonical);
  moveKeyedStateEntry(state.creatingActivityByKey, rawKey, canonical);
}

function updateTotalAfterMutation(state) {
  if (state.hasMore) {
    state.total = Math.max(Number(state.total || 0), state.items.length);
    return;
  }

  state.total = state.items.length;
}

export const listCreatorsCrmAsync = createAsyncThunk(
  "creatorsCrm/list",
  async (
    { campaignId, params = {} } = {},
    { extra, rejectWithValue }
  ) => {
    try {
      const safeCampaignId = extractCampaignId({ campaignId, params });

      if (!safeCampaignId) {
        return rejectWithValue(
          "campaignId is required to load creator CRM."
        );
      }

      const token = await extra.getAccessToken?.();

      const response = await listCreatorCrm({
        campaignId: safeCampaignId,
        token: token || "",
        params,
      });

      return {
        ...response,
        campaignId: safeCampaignId,
        items: dedupeRows(response?.items),
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Failed to load creator CRM"
      );
    }
  }
);

export const getCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/get",
  async (
    { campaignId, creatorKey } = {},
    { extra, rejectWithValue }
  ) => {
    const safeCreatorKey = canonicalKey(creatorKey);

    try {
      const safeCampaignId = norm(campaignId);

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message:
            "campaignId is required to load creator CRM details.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: "",
          message:
            "creatorKey is required to load creator CRM details.",
        });
      }

      const token = await extra.getAccessToken?.();

      const item = await getCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
      });

      const normalizedItem = normalizeRow({
        creatorKey: safeCreatorKey,
        ...(isObject(item) ? item : {}),
      });

      return {
        creatorKey: canonicalKey(normalizedItem) || safeCreatorKey,
        requestedCreatorKey: safeCreatorKey,
        item: normalizedItem,
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: safeCreatorKey,
        message:
          error?.message ||
          "Failed to load creator CRM details",
      });
    }
  }
);

export const createCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/create",
  async (
    { campaignId, creatorKey, payload = {} } = {},
    { extra, rejectWithValue }
  ) => {
    const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

    try {
      const safeCampaignId = extractCampaignId({
        campaignId,
        payload,
      });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message:
            "campaignId is required to create creator CRM.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: "",
          message:
            "creatorKey is required to create creator CRM.",
        });
      }

      const token = await extra.getAccessToken?.();

      const item = await createCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      const normalizedItem = normalizeRow({
        creatorKey: safeCreatorKey,
        ...payload,
        ...(isObject(item) ? item : {}),
      });

      return {
        creatorKey: canonicalKey(normalizedItem) || safeCreatorKey,
        requestedCreatorKey: safeCreatorKey,
        item: normalizedItem,
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: safeCreatorKey,
        message:
          error?.message || "Failed to create creator CRM",
      });
    }
  }
);

export const updateCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/update",
  async (
    { campaignId, creatorKey, payload = {} } = {},
    { extra, rejectWithValue }
  ) => {
    const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

    try {
      const safeCampaignId = extractCampaignId({
        campaignId,
        payload,
      });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message:
            "campaignId is required to update creator CRM.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: "",
          message:
            "creatorKey is required to update creator CRM.",
        });
      }

      const token = await extra.getAccessToken?.();

      const item = await updateCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      const normalizedItem = normalizeRow({
        creatorKey: safeCreatorKey,
        ...payload,
        ...(isObject(item) ? item : {}),
      });

      return {
        creatorKey: canonicalKey(normalizedItem) || safeCreatorKey,
        requestedCreatorKey: safeCreatorKey,
        item: normalizedItem,
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: safeCreatorKey,
        message:
          error?.message || "Failed to update creator CRM",
      });
    }
  }
);

export const listCreatorCrmActivitiesAsync = createAsyncThunk(
  "creatorsCrm/listActivities",
  async (
    { campaignId, creatorKey, params = {} } = {},
    { extra, rejectWithValue }
  ) => {
    const safeCreatorKey = canonicalKey(creatorKey);

    try {
      const safeCampaignId = norm(campaignId);

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message:
            "campaignId is required to load creator CRM activities.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: "",
          message:
            "creatorKey is required to load creator CRM activities.",
        });
      }

      const token = await extra.getAccessToken?.();

      const response = await listCreatorCrmActivities({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        params,
      });

      const items = Array.isArray(response?.items)
        ? response.items.map(normalizeActivity)
        : [];

      return {
        creatorKey: safeCreatorKey,
        items,
        total:
          Number(response?.total ?? items.length) ||
          items.length,
        unavailable: Boolean(response?.unavailable),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: safeCreatorKey,
        message:
          error?.message ||
          "Failed to load creator CRM activities",
      });
    }
  }
);

export const createCreatorCrmActivityAsync = createAsyncThunk(
  "creatorsCrm/createActivity",
  async (
    { campaignId, creatorKey, payload = {} } = {},
    { extra, rejectWithValue }
  ) => {
    const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

    try {
      const safeCampaignId = extractCampaignId({
        campaignId,
        payload,
      });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message:
            "campaignId is required to create creator CRM activity.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: "",
          message:
            "creatorKey is required to create creator CRM activity.",
        });
      }

      const token = await extra.getAccessToken?.();

      const response = await createCreatorCrmActivity({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      return {
        creatorKey: safeCreatorKey,
        activity: normalizeActivity(response || payload),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: safeCreatorKey,
        message:
          error?.message ||
          "Failed to create creator CRM activity",
      });
    }
  }
);

const initialState = {
  campaignId: "",
  items: [],
  total: 0,
  summary: {},
  cursor: "",
  hasMore: false,
  unavailable: false,

  loading: false,
  error: "",
  listRequestId: "",

  savingByKey: {},
  updateErrorByKey: {},

  detailByKey: {},
  detailLoadingByKey: {},
  detailErrorByKey: {},

  activitiesByKey: {},
  activitiesLoadingByKey: {},
  activitiesErrorByKey: {},
  activitiesUnavailableByKey: {},
  creatingActivityByKey: {},
};

const creatorsCrmSlice = createSlice({
  name: "creatorsCrm",
  initialState,

  reducers: {
    clearCreatorsCrmError(state) {
      state.error = "";
    },

    clearCreatorCrmUpdateError(state, action) {
      const key = canonicalKey(action.payload);
      if (key) delete state.updateErrorByKey[key];
    },

    clearCreatorCrmDetailError(state, action) {
      const key = canonicalKey(action.payload);
      if (key) delete state.detailErrorByKey[key];
    },

    clearCreatorCrmActivitiesError(state, action) {
      const key = canonicalKey(action.payload);
      if (key) delete state.activitiesErrorByKey[key];
    },

    resetCreatorsCrmState() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(listCreatorsCrmAsync.pending, (state, action) => {
        state.loading = true;
        state.error = "";
        state.listRequestId = action.meta.requestId;
        state.campaignId = extractCampaignId(action.meta.arg || {});
      })

      .addCase(listCreatorsCrmAsync.fulfilled, (state, action) => {
        if (
          state.listRequestId &&
          state.listRequestId !== action.meta.requestId
        ) {
          return;
        }

        const items = dedupeRows(action.payload?.items);

        state.loading = false;
        state.error = "";
        state.listRequestId = "";
        state.campaignId = norm(
          action.payload?.campaignId || state.campaignId
        );
        state.items = items;
        state.total =
          Number(action.payload?.total ?? items.length) ||
          items.length;
        state.summary = isObject(action.payload?.summary)
          ? action.payload.summary
          : {};
        state.cursor = norm(
          action.payload?.nextCursor ||
            action.payload?.cursor ||
            action.payload?.lastKey
        );
        state.hasMore =
          typeof action.payload?.hasMore === "boolean"
            ? action.payload.hasMore
            : Boolean(state.cursor);
        state.unavailable = Boolean(action.payload?.unavailable);
      })

      .addCase(listCreatorsCrmAsync.rejected, (state, action) => {
        if (
          state.listRequestId &&
          state.listRequestId !== action.meta.requestId
        ) {
          return;
        }

        state.loading = false;
        state.listRequestId = "";
        state.error = errorMessage(
          action,
          "Failed to load creator CRM"
        );
      })

      .addCase(getCreatorCrmAsync.pending, (state, action) => {
        const key = canonicalKey(action.meta.arg?.creatorKey);

        if (key) {
          state.detailLoadingByKey[key] = true;
          delete state.detailErrorByKey[key];
        }
      })

      .addCase(getCreatorCrmAsync.fulfilled, (state, action) => {
        const requestedKey = canonicalKey(
          action.payload?.requestedCreatorKey
        );
        const key = canonicalKey(
          action.payload?.creatorKey ||
            action.payload?.item
        );

        if (!key) return;

        syncKeyAliases(state, requestedKey, key);

        state.detailLoadingByKey[key] = false;
        delete state.detailErrorByKey[key];
        state.detailByKey[key] = mergeRows(
          state.detailByKey[key],
          action.payload?.item
        );
        state.items = upsertRow(
          state.items,
          action.payload?.item || {}
        );
        updateTotalAfterMutation(state);
      })

      .addCase(getCreatorCrmAsync.rejected, (state, action) => {
        const key = resolveActionKey(action);
        const message = errorMessage(
          action,
          "Failed to load creator CRM details"
        );

        if (key) {
          state.detailLoadingByKey[key] = false;
          state.detailErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(createCreatorCrmAsync.pending, (state, action) => {
        const key = resolveActionKey(action);

        if (key) {
          state.savingByKey[key] = true;
          delete state.updateErrorByKey[key];
        }
      })

      .addCase(createCreatorCrmAsync.fulfilled, (state, action) => {
        const requestedKey = canonicalKey(
          action.payload?.requestedCreatorKey
        );
        const key = canonicalKey(
          action.payload?.creatorKey ||
            action.payload?.item
        );

        if (!key) return;

        syncKeyAliases(state, requestedKey, key);
        delete state.savingByKey[key];
        delete state.updateErrorByKey[key];

        state.detailByKey[key] = mergeRows(
          state.detailByKey[key],
          action.payload?.item
        );
        state.items = upsertRow(
          state.items,
          action.payload?.item || {}
        );
        state.error = "";
        updateTotalAfterMutation(state);
      })

      .addCase(createCreatorCrmAsync.rejected, (state, action) => {
        const key = resolveActionKey(action);
        const message = errorMessage(
          action,
          "Failed to create creator CRM"
        );

        if (key) {
          delete state.savingByKey[key];
          state.updateErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(updateCreatorCrmAsync.pending, (state, action) => {
        const key = resolveActionKey(action);

        if (key) {
          state.savingByKey[key] = true;
          delete state.updateErrorByKey[key];
        }
      })

      .addCase(updateCreatorCrmAsync.fulfilled, (state, action) => {
        const requestedKey = canonicalKey(
          action.payload?.requestedCreatorKey
        );
        const key = canonicalKey(
          action.payload?.creatorKey ||
            action.payload?.item
        );

        if (!key) return;

        syncKeyAliases(state, requestedKey, key);
        delete state.savingByKey[key];
        delete state.updateErrorByKey[key];

        state.detailByKey[key] = mergeRows(
          state.detailByKey[key],
          action.payload?.item
        );
        state.items = upsertRow(
          state.items,
          action.payload?.item || {}
        );
        state.error = "";
        updateTotalAfterMutation(state);
      })

      .addCase(updateCreatorCrmAsync.rejected, (state, action) => {
        const key = resolveActionKey(action);
        const message = errorMessage(
          action,
          "Failed to update creator CRM"
        );

        if (key) {
          delete state.savingByKey[key];
          state.updateErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(
        listCreatorCrmActivitiesAsync.pending,
        (state, action) => {
          const key = canonicalKey(action.meta.arg?.creatorKey);

          if (key) {
            state.activitiesLoadingByKey[key] = true;
            delete state.activitiesErrorByKey[key];
            delete state.activitiesUnavailableByKey[key];
          }
        }
      )

      .addCase(
        listCreatorCrmActivitiesAsync.fulfilled,
        (state, action) => {
          const key = canonicalKey(action.payload?.creatorKey);
          if (!key) return;

          state.activitiesLoadingByKey[key] = false;
          delete state.activitiesErrorByKey[key];
          state.activitiesUnavailableByKey[key] = Boolean(
            action.payload?.unavailable
          );
          state.activitiesByKey[key] = Array.isArray(
            action.payload?.items
          )
            ? action.payload.items.map(normalizeActivity)
            : [];
        }
      )

      .addCase(
        listCreatorCrmActivitiesAsync.rejected,
        (state, action) => {
          const key = resolveActionKey(action);
          const message = errorMessage(
            action,
            "Failed to load creator CRM activities"
          );

          if (key) {
            state.activitiesLoadingByKey[key] = false;
            state.activitiesErrorByKey[key] = message;
          } else {
            state.error = message;
          }
        }
      )

      .addCase(
        createCreatorCrmActivityAsync.pending,
        (state, action) => {
          const key = resolveActionKey(action);

          if (key) {
            state.creatingActivityByKey[key] = true;
            delete state.activitiesErrorByKey[key];
          }
        }
      )

      .addCase(
        createCreatorCrmActivityAsync.fulfilled,
        (state, action) => {
          const key = canonicalKey(action.payload?.creatorKey);
          if (!key) return;

          delete state.creatingActivityByKey[key];
          delete state.activitiesErrorByKey[key];
          state.activitiesUnavailableByKey[key] = false;
          state.activitiesByKey[key] = prependActivity(
            state.activitiesByKey[key],
            action.payload?.activity
          );
        }
      )

      .addCase(
        createCreatorCrmActivityAsync.rejected,
        (state, action) => {
          const key = resolveActionKey(action);
          const message = errorMessage(
            action,
            "Failed to create creator CRM activity"
          );

          if (key) {
            delete state.creatingActivityByKey[key];
            state.activitiesErrorByKey[key] = message;
          } else {
            state.error = message;
          }
        }
      );
  },
});

export const {
  clearCreatorsCrmError,
  clearCreatorCrmUpdateError,
  clearCreatorCrmDetailError,
  clearCreatorCrmActivitiesError,
  resetCreatorsCrmState,
} = creatorsCrmSlice.actions;

export const selectCreatorsCrmState = (state) =>
  state?.creatorsCrm || initialState;

export const selectCreatorsCrmItems = (state) =>
  selectCreatorsCrmState(state).items || [];

export const selectCreatorsCrmTotal = (state) =>
  Number(selectCreatorsCrmState(state).total || 0);

export const selectCreatorsCrmSummary = (state) =>
  selectCreatorsCrmState(state).summary || {};

export const selectCreatorsCrmCursor = (state) =>
  selectCreatorsCrmState(state).cursor || "";

export const selectCreatorsCrmHasMore = (state) =>
  Boolean(selectCreatorsCrmState(state).hasMore);

export const selectCreatorsCrmLoading = (state) =>
  Boolean(selectCreatorsCrmState(state).loading);

export const selectCreatorsCrmError = (state) =>
  selectCreatorsCrmState(state).error || "";

export const selectCreatorsCrmSavingByKey = (state) =>
  selectCreatorsCrmState(state).savingByKey || {};

export const selectCreatorsCrmUpdateErrorsByKey = (state) =>
  selectCreatorsCrmState(state).updateErrorByKey || {};

export const selectCreatorsCrmDetailByKey = (state) =>
  selectCreatorsCrmState(state).detailByKey || {};

export const selectCreatorsCrmDetailLoadingByKey = (state) =>
  selectCreatorsCrmState(state).detailLoadingByKey || {};

export const selectCreatorsCrmDetailErrorByKey = (state) =>
  selectCreatorsCrmState(state).detailErrorByKey || {};

export const selectCreatorsCrmActivitiesByKey = (state) =>
  selectCreatorsCrmState(state).activitiesByKey || {};

export const selectCreatorsCrmActivitiesLoadingByKey = (state) =>
  selectCreatorsCrmState(state).activitiesLoadingByKey || {};

export const selectCreatorsCrmActivitiesErrorByKey = (state) =>
  selectCreatorsCrmState(state).activitiesErrorByKey || {};

export const selectCreatorsCrmActivitiesUnavailableByKey = (state) =>
  selectCreatorsCrmState(state).activitiesUnavailableByKey || {};

export const selectCreatorsCrmCreatingActivityByKey = (state) =>
  selectCreatorsCrmState(state).creatingActivityByKey || {};

export const selectCreatorCrmByKey = (state, creatorKey) => {
  const key = canonicalKey(creatorKey);
  if (!key) return null;

  const crmState = selectCreatorsCrmState(state);

  return (
    crmState.detailByKey?.[key] ||
    crmState.items?.find(
      (item) => canonicalKey(item) === key
    ) ||
    null
  );
};

export const selectCreatorCrmSaving = (state, creatorKey) => {
  const key = canonicalKey(creatorKey);
  return key
    ? Boolean(selectCreatorsCrmState(state).savingByKey?.[key])
    : false;
};

export const selectCreatorCrmUpdateError = (state, creatorKey) => {
  const key = canonicalKey(creatorKey);
  return key
    ? selectCreatorsCrmState(state).updateErrorByKey?.[key] || ""
    : "";
};

export const selectCreatorCrmActivities = (state, creatorKey) => {
  const key = canonicalKey(creatorKey);
  return key
    ? selectCreatorsCrmState(state).activitiesByKey?.[key] || []
    : [];
};

export default creatorsCrmSlice.reducer;
